import axios from 'axios'
import _ from 'lodash'

import USER from '@/api/user'
import ORDER from '@/api/order'
import UTILITIES from '@/api/utilities'
import i18n from '@/i18n'
import ls from '@/utils/ls'

import getSymbolFromCurrency from 'currency-symbol-map'
import { event, set } from 'vue-analytics'
import router from '@/router'
import { MessageBox } from 'element-ui'

const codeToName = (code) => {
  switch (code) {
    case 'zh_TW':
      return '繁體中文'
    case 'zh_CN':
      return '简体中文'
    case 'en':
      return 'English'
    case 'ja':
      return '日本語'
    case 'th':
      return 'ภาษาไทย'
    case 'ko':
      return '한국어'
    case 'vi':
      return 'Tiếng Việt'
    case 'tr':
      return 'Türkçe'
    case 'tl':
      return 'Filipino'
    case 'ta':
      return 'Tamil'
    case 'nl':
      return 'Nederlands'
    case 'my':
      return 'Myanmar'
    case 'ms':
      return 'Bahasa Malaysia'
    case 'it':
      return 'Italiano'
    case 'id':
      return 'Bahasa Indonesia'
    case 'hi':
      return 'हिन्दी'
    case 'fr':
      return 'Français'
    case 'es':
      return 'Español'
    case 'de':
      return 'Deutsch'
    case 'cs':
      return 'Čeština'
    case 'ar':
      return 'العربية'
    default:
      break
  }
}

const codeToFlag = (code) => {
  switch (code) {
    case 'zh_TW':
      return 'tw'
    case 'zh_CN':
      return 'cn'
    case 'en':
      return 'us'
    case 'ja':
      return 'jp'
    case 'th':
      return 'th'
    case 'ko':
      return 'kr'
    case 'vi':
      return 'vn'
    case 'tr':
      return 'tur'
    case 'tl':
      return 'ph'
    case 'ta':
      return 'Tamil'
    case 'nl':
      return 'nl'
    case 'my':
      return 'Myanmar'
    case 'ms':
      return 'my'
    case 'it':
      return 'it'
    case 'id':
      return 'id'
    case 'hi':
      return 'in'
    case 'fr':
      return 'fr'
    case 'es':
      return 'es'
    case 'de':
      return 'de'
    case 'cs':
      return 'cz'
    case 'ar':
      return 'sa'
    default:
      break
  }
}

const codeToSort = (code) => {
  switch (code) {
    case 'zh_TW':
      return 1
    case 'zh_CN':
      return 2
    case 'en':
      return 3
    case 'ja':
      return 4
    case 'th':
      return 6
    case 'ko':
      return 5
    case 'vi':
      return 7
    case 'tr':
      return 10
    case 'tl':
      return 11
    case 'nl':
      return 12
    case 'ms':
      return 8
    case 'it':
      return 13
    case 'id':
      return 14
    case 'hi':
      return 15
    case 'fr':
      return 9
    case 'es':
      return 16
    case 'de':
      return 17
    case 'cs':
      return 18
    case 'ar':
      return 19
    default:
      break
  }
}

const langList = Object.keys(i18n.messages).reduce((acc, curr) => {
  acc.push({
    code: curr,
    name: codeToName(curr),
    flag: codeToFlag(curr),
    order: codeToSort(curr)
  })
  return acc
}, [])

// initial state
const state = {
  isMobileMenuOpen: false,
  isMobileSearchBarOpen: false,
  isLoading: false,
  countries: [],
  phoneCodes: [],
  profileCancelToken: false,
  userPath: {
    hotelId: null
  },
  // $i18n.availableLocales
  langsList: langList.sort((a, b) => (a.order - b.order)),
  // langsList: [
  //   {
  //     code: 'zh_TW',
  //     name: '中文'
  //   },
  //   {
  //     code: 'en',
  //     name: 'English'
  //   },
  //   {
  //     code: 'ja',
  //     name: '日本語'
  //   },
  //   {
  //     code: 'th',
  //     name: 'ไทย'
  //   }
  // ],
  selectedLang: 'zh_TW',
  selectedCurrency: 'TWD',
  profile: {},
  token: {
    accessToken: null,
    refreshToken: null
  },
  favoriteHotelList: [],
  coupons: [],
  couponsHistory: [],
  points: [],
  pointsRecords: [],
  night: {
    count: null
  },
  prevStepIsLogout: false
}

// getters
const getters = {
  userPath: (state) => {
    return state.userPath
  },
  langsList: (state) => {
    return state.langsList
  },
  currenciesList: (state, getters, rootStates, rootGetters) => {
    let list = []
    if (rootGetters.currencySelectable) {
      for (const currency of rootGetters.currencySelectable) {
        list.push({
          code: currency.code,
          name: currency.code,
          icon: getSymbolFromCurrency(currency.code),
          currency_name: currency.name
        })
      }
    }
    return list
  },
  selectedLang: (state) => {
    i18n.locale = state.selectedLang
    return state.selectedLang
  },
  selectedCurrency: (state) => {
    return state.selectedCurrency
  },
  selectedCurrencyName: (state, getters) => {
    const target = _.find(getters.currenciesList, currency => currency.code === state.selectedCurrency)
    if (target) {
      return `${target.name} ${getters.currencySign}`
    }
    return null
  },
  currencySign: (state) => {
    return getSymbolFromCurrency(state.selectedCurrency)
  },
  selectedLangName: (state) => {
    const target = _.find(state.langsList, lang => lang.code === state.selectedLang)
    return target.name
  },
  selectLangFlagCode: (state) => {
    return codeToFlag(state.selectedLang)
  },
  isLoading: (state) => {
    return state.isLoading
  },
  isLogin: (state) => {
    return !state.isLoading && !_.isEmpty(state.profile)
  },
  isMobileMenuOpen: (state) => {
    return state.isMobileMenuOpen
  },
  isMobileSearchBarOpen: (state) => {
    return state.isMobileSearchBarOpen
  },
  isPremium: (state, getters) => {
    return getters.isLogin && state.profile.isPremium
  },
  premiumUrl: () => {
    return process.env.VUE_APP_PREMIUM_SITE
  },
  premiumUrlAddon: () => {
    return `${process.env.VUE_APP_PREMIUM_SITE}/auth`
  },
  premiumUrlRedirect: (state, getters) => {
    const url = new URL(process.env.VUE_APP_PREMIUM_SITE + '/redirect')
    const params = new URLSearchParams(url.search)
    const query = {
      source: 'journey',
      to: getters.isPremium ? 'account' : 'subscribe',
      login: getters.isLogin,
      redirect_url: window.location.href
    }
    for (const [key, value] of Object.entries(query)) {
      params.append(key, value)
    }
    return url.toString() + '?' + params.toString()
  },
  isLoadingProfile: (state) => {
    return _.isEmpty(state.profile)
  },
  profile: (state) => {
    return state.profile
  },
  coupons: (state) => {
    return state.coupons
  },
  points: (state) => {
    return state.points
  },
  night: (state) => {
    return state.night
  },
  couponsHistory: (state) => {
    return state.couponsHistory
  },
  favoriteHotelList: (state) => {
    return state.favoriteHotelList
  },
  countries: (state) => {
    return state.countries
  },
  phoneCodes: (state) => {
    return state.phoneCodes
  },
  prevStepIsLogout: (state) => {
    return state.prevStepIsLogout
  },
  pointsRecords: (state) => {
    return state.pointsRecords
  }
}

// actions
const actions = {
  updateProfile ({ commit, getters }, data) {
    return USER.updateProfile(data)
  },
  updatePassword ({ commit, getters }, data) {
    return USER.updatePassword(data)
  },
  loginPremium ({ commit }) {
    return USER.loginPremium()
  },
  goLogin ({ commit, getters }) {
    commit('SET_LOADING', true)
    commit('SET_REDIRECT_URL', window.location.href, { root: true })
    event('click', 'User Click Login Button')
    window.location = `${process.env.VUE_APP_API}/v2/auth/login`
  },
  fetchProfile ({ commit }) {
    if (state.profileCancelToken) {
      state.profileCancelToken.cancel('Cancel profile fetching.')
    }
    commit('SET_LOADING', true)
    commit('SET_PROFILE_CANCEL_TOKEN')
    console.debug('[user] Before fetch profile: ', ls.get('owlJourney').user.token.accessToken)
    return USER.profile(state.profileCancelToken).then(res => {
      if (res.status === 200) {
        commit('SET_PROFILE', res.data)
        set('userId', res.data.uuid)
        event('user', 'User Login Fetch Profile', res.data.uuid)
      } else {
        commit('REMOVE_USER_STATE')
      }
      commit('SET_LOADING', false)
      return res
    }, err => {
      console.warn('fetchProfile', err)
      return err
    })
  },
  toggleMobileMenu ({ commit }, isOpen) {
    commit('MOBILE_MENU_STATUS', isOpen)
  },
  switchLang ({ commit, dispatch }, lang) {
    commit('SET_LANG', lang)
    // dispatch('search/fetchFilterOptions', null, { root: true })
  },
  switchCurrency ({ commit, dispatch }, currency) {
    commit('SET_CURRENCY', currency)
    // dispatch('search/fetchFilterOptions', null, { root: true })
  },
  getCoupons ({ commit }, params) {
    return USER.fetchCoupons(params).then(res => {
      commit('SET_COUPONS', res.data)
      return res
    })
  },
  addCoupons ({ commit }, code) {
    return USER.addCoupon({ code }).then(res => {
      return res
    })
  },
  getNight ({ commit }) {
    return USER.fetchNight().then(res => {
      commit('SET_NIGHT', res.data)
      return res
    })
  },
  addCouponVaiNight ({ commit }) {
    return USER.addCouponViaNight().then(res => {
      return res
    })
  },
  getCouponNightPreview ({ commit }) {
    return USER.fetchCouponNightPreview().then(res => {
      return res
    })
  },
  getCouponsHistory ({ commit }, params) {
    return USER.fetchCouponsHistory(params).then(res => {
      commit('SET_COUPONS_HISTORY', res.data)
      return res
    })
  },
  getOrders ({ commit }, params) {
    return ORDER.orderList(params)
  },
  getOrder ({ commit }, params) {
    return ORDER.order(params)
  },
  goLogout ({ dispatch, commit }) {
    // console.log('go logout')
    commit('SET_LOADING', true)
    commit('SET_PREV_STEP_IS_LOGOUT', true)
    return USER.logout().then(res => {
      commit('SET_REDIRECT_URL', window.location.href, { root: true })
      // 清空個人資訊
      commit('SET_PROFILE', null)
      commit('SET_TOKEN', {
        accessToken: null,
        refreshToken: null
      })
      // 清空查詢條件
      // dispatch('search/fetchFilterOptions', null, { root: true })
      // 清空購物車 & 訂單
      commit('order/CLEAR_CARTS', null, { root: true })
      commit('order/CLEAR_ADDON_CARTS', null, { root: true })
      commit('order/CLEAR_COUPON', null, { root: true })
      // 跳轉
      const logoutUrl = res.data.logoutUrl
      window.location = logoutUrl
    })
  },
  fetchFavorite ({ commit }) {
    return USER.fetchFavorites()
      .then(res => {
        commit('SET_FAVORITE_LIST', res.data.hotels)
      })
  },
  addFavorite ({ commit, dispatch, getters }, hotelUUID) {
    const hotel = getters['hotels/hotel']
    window.fbq('track', 'AddToWishlist', {
      content_ids: [hotelUUID],
      content_type: 'hotel',
      content_name: hotel?.hotelName || '',
      currency: hotel?.currency || 'TWD'
    })
    commit('hotels/SET_LOADING_FAVORITE', true, { root: true })
    if (getters.isLogin) {
      return USER.addFavorite({ hotel_uuid: hotelUUID }).then(res => {
        if (res.status === 200) {
          commit('hotels/SET_LOADING_FAVORITE', false, { root: true })
          return res.data
        }
      })
    } else {
      MessageBox.confirm(i18n.t('notLogin.title'), i18n.t('notLogin.msg2'), {
        type: 'warning',
        confirmButtonText: i18n.t('login')
      }).then(function () {
        dispatch('goLogin')
      }).finally(function () {
        commit('hotels/SET_LOADING_FAVORITE', false, { root: true })
      })
    }
  },
  removeFavorite ({ commit }, hotelUUID) {
    commit('hotels/SET_LOADING_FAVORITE', true, { root: true })
    return USER.removeFavorite({ hotel_uuid: hotelUUID })
      .then(res => {
        if (res.status === 200) {
          commit('hotels/SET_LOADING_FAVORITE', false, { root: true })
          return res.data
        }
      })
  },
  getCountryList: function ({ commit }) {
    return USER.getCountryList().then(res => {
      commit('SET_COUNTRIES', res.data)
      return res.data
    })
  },
  sendInvitation: function ({ commit }, email) {
    return USER.sendInvitation({ email })
      .then(res => {
        if (res.status === 200) {
          return res
        }
      })
  },
  subscribeEmail: function ({ commit }, email) {
    return USER.subscribeEmail({ email })
      .then(res => {
        if (res.status === 200) {
          return res
        }
      })
  },
  fetchPhoneCodes: function ({ commit }) {
    return UTILITIES.phoneCode()
      .then(res => {
        if (res.status === 200) {
          commit('SET_PHONE_CODES', res.data)
        }
      })
  },
  fetchPointsRecords: function ({ commit }, params) {
    return USER.fetchPointsRecords(params)
      .then(res => {
        if (res.status === 200) {
          commit('SET_POINTS_RECORDS', res.data.points)
          return res
        }
      })
  },
  setLoginLoading: function ({ commit }, open) {
    commit('SET_LOADING', open)
  }
}

// mutations
const mutations = {
  MOBILE_MENU_STATUS (state, payload) {
    if (typeof payload === 'boolean') {
      state.isMobileMenuOpen = payload
    } else {
      state.isMobileMenuOpen = !state.isMobileMenuOpen
    }
  },
  MOBILE_SEARCH_BAR_STATUS (state, isOpen) {
    state.isMobileSearchBarOpen = isOpen
  },
  REMOVE_USER_STATE (state) {
    state.isLogin = false
    state.profile = {}
  },
  SET_LANG: function (state, payload) {
    i18n.locale = state.selectedLang = payload
    router.replace({
      query: {
        ...router.history.current.query,
        lang: payload
      }
    })
    window.location.reload()
  },
  SET_LANG_WITHOUT_RELOAD: function (state, payload) {
    i18n.locale = state.selectedLang = payload
  },
  SET_CURRENCY: function (state, payload) {
    state.selectedCurrency = payload
  },
  SET_USER_PATH_HOTEL: function (state, payload) {
    state.userPath.hotelId = payload
  },
  SET_LOADING: function (state, payload) {
    state.isLoading = payload
  },
  SET_PROFILE_CANCEL_TOKEN: function () {
    state.profileCancelToken = axios.CancelToken.source()
  },
  SET_TOKEN: function (state, token) {
    const { accessToken, refreshToken } = token
    const tokenLocalStorage = ls.get('owlJourney')

    state.token.accessToken = accessToken
    state.token.refreshToken = refreshToken
    tokenLocalStorage.user.token = token
    ls.set('owlJourney', tokenLocalStorage)
    console.debug('[user] token set: ', state.token.accessToken)
  },
  SET_PROFILE: function (state, payload) {
    if (payload && (!payload.phoneCode || Number(payload.phoneCode) === 0)) {
      payload.phoneCode = 886
    }
    state.profile = payload
  },
  SET_FAVORITE_LIST: function (state, payload) {
    state.favoriteHotelList = payload
  },
  SET_COUPONS: function (state, payload) {
    state.coupons = payload
  },
  SET_COUPONS_HISTORY: function (state, payload) {
    state.couponsHistory = payload
  },
  SET_NIGHT: function (state, payload) {
    state.night = payload
  },
  SET_COUNTRIES: function (state, payload) {
    state.countries = payload
  },
  SET_PHONE_CODES: function (state, payload) {
    state.phoneCodes = payload
  },
  SET_PREV_STEP_IS_LOGOUT: function (state, payload) {
    // console.log('SET_PREV_STEP_IS_LOGOUT', payload)
    state.prevStepIsLogout = payload
  },
  SET_POINTS_RECORDS: function (state, payload) {
    state.pointsRecords = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
